import { auth, db } from "./firebase.js";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  updateEmail,
  sendPasswordResetEmail 
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";

/**
 * Limpa e formata o CPF para conter apenas dígitos
 */
function cleanCPF(cpf) {
  return typeof cpf === "string" ? cpf.replace(/\D/g, "") : "";
}

/**
 * Gera um e-mail virtual a partir do CPF do aluno
 */
function getEmailFromCPF(cpf) {
  const cleaned = cleanCPF(cpf);
  return `${cleaned}@academiadoextrajudicial.com`;
}

/**
 * Configurações do link de redefinição de senha (retorna ao login com modal aberto).
 */
const PASSWORD_RESET_ACTION_CODE_SETTINGS = {
  url: "https://academiadoextrajudicial.web.app/?action=login",
  handleCodeInApp: false,
};

/**
 * Cadastra um novo ALUNO (Colaborador) com CPF, senha própria, cartório e e-mail de recuperação.
 * Sob o capô, cria um login de e-mail/senha com o e-mail real (de recuperação).
 * @param {string} name 
 * @param {string} cpf 
 * @param {string} password 
 * @param {string} serventia 
 * @param {string} recoveryEmail 
 * @returns {Promise<User>}
 */
export async function signUpStudent(name, cpf, password, serventia, recoveryEmail) {
  if (!auth || !db) {
    throw new Error("Serviços do Firebase não estão inicializados. Verifique seu arquivo .env.");
  }

  const cleanedCPF = cleanCPF(cpf);
  if (cleanedCPF.length !== 11) {
    throw new Error("CPF inválido. Deve conter 11 dígitos.");
  }

  if (!name || !name.trim()) {
    throw new Error("Nome é obrigatório.");
  }

  if (!password || password.length < 6) {
    throw new Error("A senha deve conter no mínimo 6 caracteres.");
  }

  if (!recoveryEmail) {
    throw new Error("E-mail de recuperação é obrigatório.");
  }

  const authEmail = recoveryEmail.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail)) {
    throw new Error("E-mail de recuperação inválido.");
  }

  const virtualEmail = getEmailFromCPF(cleanedCPF);
  const serventiaFinal = serventia || "Ambos";

  // Cadastra no Auth com o e-mail real (de recuperação) para permitir o reset de senha
  const userCredential = await createUserWithEmailAndPassword(auth, authEmail, password);
  const user = userCredential.user;

  // Salva perfil de estudante no Firestore com o e-mail de recuperação e o cartório
  const userProfile = {
    uid: user.uid,
    name: name.trim(),
    cpf: cleanedCPF,
    email: recoveryEmail, // E-mail de recuperação do aluno
    authEmail: authEmail, // E-mail real usado no Firebase Auth (login/reset)
    serventia: serventiaFinal, // Cartório ao qual pertence
    virtualEmail: virtualEmail, // E-mail virtual derivado do CPF (referência/legado)
    role: "colaborador", // Papel fixo de Aluno
    branchId: "branch_main",
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    consentLGPDAt: new Date().toISOString(),
    points: 0,
    streakDays: 1,
    streakLastAccess: new Date().toISOString(),
    badges: [],
    status: "active"
  };

  await setDoc(doc(db, "users", user.uid), userProfile);
  await setDoc(doc(db, "cpf_login", cleanedCPF), { authEmail: authEmail, uid: user.uid });
  return { user, profile: userProfile };
}

/**
 * Obtém o e-mail de login (Auth) de um aluno pelo CPF, via coleção pública cpf_login.
 * Lida com o fluxo de login/recuperação que acontece ANTES da autenticação.
 * @param {string} cpf 
 * @returns {Promise<string|null>}
 */
export async function getAuthEmailByCPF(cpf) {
  if (!db) return null;
  const cleaned = cleanCPF(cpf);
  if (cleaned.length !== 11) return null;
  try {
    const snap = await getDoc(doc(db, "cpf_login", cleaned));
    if (snap.exists()) {
      const data = snap.data();
      return typeof data.authEmail === "string" ? data.authEmail : null;
    }
  } catch (error) {
    console.error("Erro ao buscar e-mail de login por CPF:", error);
  }
  return null;
}

/**
 * Realiza o login do aluno usando CPF e senha.
 * Resolve o e-mail real (authEmail) via cpf_login; conta legada usa o e-mail virtual.
 * @param {string} cpf 
 * @param {string} password 
 * @returns {Promise<UserCredential>}
 */
export async function signInStudent(cpf, password) {
  if (!auth) {
    throw new Error("Serviços do Firebase não estão inicializados.");
  }

  const cleanedCPF = cleanCPF(cpf);
  if (cleanedCPF.length !== 11) {
    throw new Error("CPF inválido. Deve conter 11 dígitos.");
  }

  if (!password) {
    throw new Error("Informe sua senha.");
  }

  const authEmail = await getAuthEmailByCPF(cleanedCPF);
  const email = authEmail || getEmailFromCPF(cleanedCPF);

  const credential = await signInWithEmailAndPassword(auth, email, password);

  try {
    const profile = await getUserProfile(credential.user.uid);
    if (profile) {
      await migrateLegacyAuthEmail(credential.user, profile);
    }
  } catch (error) {
    console.warn("Migração de e-mail legado não realizada:", error.code || error.message);
  }

  return credential;
}

/**
 * Migra contas legadas (Auth email virtual) para o e-mail real do perfil.
 * Chamada após login bem-sucedido — primeira vez que a conta é acessada pós-deploy.
 * @param {object} user 
 * @param {object} profile 
 * @returns {Promise<void>}
 */
async function migrateLegacyAuthEmail(user, profile) {
  if (!auth || !profile) return;
  const currentEmail = user.email;
  const isVirtual = profile.virtualEmail && currentEmail === profile.virtualEmail;
  const recoveryEmail = profile.email;
  if (!isVirtual || !recoveryEmail || recoveryEmail === currentEmail) return;

  await updateEmail(auth.currentUser, recoveryEmail);
  await setDoc(doc(db, "users", user.uid), { authEmail: recoveryEmail }, { merge: true });
  if (profile.cpf) {
    await setDoc(doc(db, "cpf_login", profile.cpf), { authEmail: recoveryEmail, uid: user.uid });
  }
}

/**
 * Cadastra um administrador ou instrutor usando e-mail e senha convencionais.
 * @param {string} name 
 * @param {string} email 
 * @param {string} password 
 * @param {string} role ('admin' | 'instrutor')
 * @returns {Promise<User>}
 */
export async function signUpStaff(name, email, password, role) {
  if (!auth || !db) {
    throw new Error("Serviços do Firebase não estão inicializados.");
  }

  if (role !== "admin" && role !== "instrutor") {
    throw new Error("Papel de acesso não permitido.");
  }

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const userProfile = {
    uid: user.uid,
    name,
    email,
    role, // 'admin' | 'instrutor'
    branchId: "branch_main",
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    consentLGPDAt: new Date().toISOString(),
    points: 0,
    streakDays: 0,
    badges: [],
    status: "active"
  };

  await setDoc(doc(db, "users", user.uid), userProfile);
  return { user, profile: userProfile };
}

/**
 * Realiza o login convencional para Administradores e Instrutores
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<UserCredential>}
 */
export async function signInStaff(email, password) {
  if (!auth) {
    throw new Error("Serviços do Firebase não estão inicializados.");
  }
  return await signInWithEmailAndPassword(auth, email, password);
}

/**
 * Encerra a sessão do usuário.
 * @returns {Promise<void>}
 */
export async function signOut() {
  if (!auth) return;
  return await firebaseSignOut(auth);
}

/**
 * Envia o link de redefinição de senha para o e-mail real do aluno (via CPF).
 * Sem erro específico quando o CPF não é encontrado (evita enumeração de contas).
 * @param {string} cpf 
 * @returns {Promise<void>}
 */
export async function sendPasswordResetForStudent(cpf) {
  if (!auth) {
    throw new Error("Serviços do Firebase não estão inicializados.");
  }

  const authEmail = await getAuthEmailByCPF(cpf);
  if (authEmail) {
    await sendPasswordResetEmail(auth, authEmail, PASSWORD_RESET_ACTION_CODE_SETTINGS);
  }
}

/**
 * Envia o link de redefinição de senha para um e-mail de staff (admin/instrutor).
 * @param {string} email 
 * @returns {Promise<void>}
 */
export async function sendPasswordResetForStaff(email) {
  if (!auth) {
    throw new Error("Serviços do Firebase não estão inicializados.");
  }

  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    throw new Error("E-mail inválido.");
  }

  await sendPasswordResetEmail(auth, email.trim(), PASSWORD_RESET_ACTION_CODE_SETTINGS);
}

/**
 * Obtém o perfil de dados do usuário salvo no Firestore.
 * @param {string} uid 
 * @returns {Promise<object|null>}
 */
export async function getUserProfile(uid) {
  if (!db) return null;
  try {
    const docSnap = await getDoc(doc(db, "users", uid));
    if (docSnap.exists()) {
      return docSnap.data();
    }
  } catch (error) {
    console.error("Erro ao buscar perfil do usuário no Firestore:", error);
  }
  return null;
}

/**
 * Inscreve-se para escutar mudanças no estado de autenticação do Firebase.
 * @param {function} callback 
 * @returns {function} unsubscribe
 */
export function subscribeToAuth(callback) {
  if (!auth) {
    console.warn("⚠️ Executando em modo de demonstração. Sessão local simulada.");
    setTimeout(() => {
      callback({
        uid: "demo-user-id",
        email: "demo@cartorio.com.br",
        profile: {
          name: "Renato Rosa (Demo)",
          role: "colaborador",
          points: 120,
          streakDays: 5
        }
      });
    }, 100);
    return () => {};
  }

  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const profile = await getUserProfile(user.uid);
      callback({ ...user, profile });
    } else {
      callback(null);
    }
  });
}
